import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Password } from 'primereact/password';
import { DialogHeader } from '@/components/dialog/dialog-header';
import { useToastContext } from '@/layout/context/toast-context';
import { changeAccountPassword } from '@/services/auth/changePasswordApi';
import { getErrorMessage } from '@/shared/validation/validation';
const INITIAL_VALUES = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
};
const changePasswordSchema = Yup.object({
    currentPassword: Yup.string().trim().required('Current password is required'),
    newPassword: Yup.string()
        .trim()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character')
        .notOneOf([Yup.ref('currentPassword')], 'New password must differ from the current password')
        .required('New password is required'),
    confirmPassword: Yup.string()
        .trim()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required('Repeat password is required'),
});
export function ChangePasswordDialog({ visible, onHide }) {
    const formId = useId().replace(/:/g, '');
    const { showToast } = useToastContext();
    const formik = useFormik({
        initialValues: INITIAL_VALUES,
        validationSchema: changePasswordSchema,
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: async (values, { setSubmitting, setStatus, resetForm }) => {
            setStatus(undefined);
            try {
                await changeAccountPassword({
                    currentPassword: values.currentPassword,
                    newPassword: values.newPassword,
                });
                showToast({
                    severity: 'success',
                    summary: 'Password updated',
                    detail: 'Your password was changed successfully.',
                });
                resetForm();
                onHide();
            }
            catch (e) {
                const detail = e instanceof Error ? e.message : 'Could not change password.';
                setStatus(detail);
            }
            finally {
                setSubmitting(false);
            }
        },
    });
    useEffect(() => {
        if (!visible) {
            formik.resetForm();
            void formik.setStatus(undefined);
        }
        // Reset only when the dialog closes — not on every formik change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);
    const handleHide = useCallback(() => {
        onHide();
    }, [onHide]);
    const fieldError = (name) => getErrorMessage(name, formik);
    const submitError = typeof formik.status === 'string' ? formik.status : undefined;
    return (_jsx(Dialog, { header: _jsx(DialogHeader, { icon: "pi pi-lock", label: "Change password" }), visible: visible, modal: true, draggable: false, resizable: false, dismissableMask: true, style: { width: '96vw', maxWidth: '32rem' }, onHide: handleHide, children: _jsxs("form", { id: `${formId}-change-password`, className: "flex flex-column gap-3", onSubmit: formik.handleSubmit, noValidate: true, children: [_jsx("p", { className: "m-0 text-color-secondary text-sm line-height-3", children: "Enter your current password, then choose a new one. You will stay signed in after the change." }), submitError ? _jsx(Message, { severity: "error", className: "w-full", text: submitError }) : null, _jsxs("div", { className: "field m-0", children: [_jsx("label", { htmlFor: `${formId}-current`, className: "block text-900 font-medium mb-2", children: "Current password" }), _jsx(Password, { id: `${formId}-current`, inputId: `${formId}-current`, name: "currentPassword", value: formik.values.currentPassword, onChange: (e) => {
                                if (submitError)
                                    void formik.setStatus(undefined);
                                void formik.setFieldValue('currentPassword', e.target.value);
                            }, onBlur: formik.handleBlur, toggleMask: true, feedback: false, className: `w-full ${fieldError('currentPassword') ? 'p-invalid' : ''}`, inputClassName: "w-full", placeholder: "Current password", autoComplete: "current-password" }), fieldError('currentPassword') ? (_jsx("small", { className: "p-error block mt-1", children: fieldError('currentPassword') })) : null] }), _jsxs("div", { className: "field m-0", children: [_jsx("label", { htmlFor: `${formId}-new`, className: "block text-900 font-medium mb-2", children: "New password" }), _jsx(Password, { id: `${formId}-new`, inputId: `${formId}-new`, name: "newPassword", value: formik.values.newPassword, onChange: (e) => {
                                if (submitError)
                                    void formik.setStatus(undefined);
                                void formik.setFieldValue('newPassword', e.target.value);
                            }, onBlur: formik.handleBlur, toggleMask: true, feedback: false, className: `w-full ${fieldError('newPassword') ? 'p-invalid' : ''}`, inputClassName: "w-full", placeholder: "New password", autoComplete: "new-password" }), fieldError('newPassword') ? (_jsx("small", { className: "p-error block mt-1", children: fieldError('newPassword') })) : null] }), _jsxs("div", { className: "field m-0", children: [_jsx("label", { htmlFor: `${formId}-confirm`, className: "block text-900 font-medium mb-2", children: "Repeat new password" }), _jsx(Password, { id: `${formId}-confirm`, inputId: `${formId}-confirm`, name: "confirmPassword", value: formik.values.confirmPassword, onChange: (e) => {
                                if (submitError)
                                    void formik.setStatus(undefined);
                                void formik.setFieldValue('confirmPassword', e.target.value);
                            }, onBlur: formik.handleBlur, toggleMask: true, feedback: false, className: `w-full ${fieldError('confirmPassword') ? 'p-invalid' : ''}`, inputClassName: "w-full", placeholder: "Repeat new password", autoComplete: "new-password" }), fieldError('confirmPassword') ? (_jsx("small", { className: "p-error block mt-1", children: fieldError('confirmPassword') })) : null] }), _jsxs("div", { className: "flex justify-content-end gap-2 pt-1", children: [_jsx(Button, { type: "button", label: "Cancel", severity: "secondary", text: true, disabled: formik.isSubmitting, onClick: handleHide }), _jsx(Button, { type: "submit", label: "Update password", icon: "pi pi-check", loading: formik.isSubmitting, disabled: formik.isSubmitting })] })] }) }));
}
